import { replace as replacePath, push as updatePath } from 'react-router-redux';
import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { select, put, call, takeEvery, spawn } from 'redux-saga/effects';
import { setMsaId } from 'redux/modules/features/homepage';
import { geodetectMsa, scrollToTop } from 'redux/saga/helpers';
import { writeSessionStorage } from 'redux/saga/helpers/local-storage';
import { getMsaFromPath } from 'redux/saga/helpers/msas';
import { cms, userProfile } from 'redux/saga/entities/bound';
import { isLoggedIn } from 'redux/saga/selectors';
import { memberStatus } from 'enums/memberStatus';
import { deepExtract } from 'helpers/deep';
import { segment } from 'helpers/segment';
import { trackGoogleTagManagerEvent } from 'components/GoogleTagManager/GoogleTagManager';
import { doLoadCmsModules } from './cms-modules.saga';

export const LOAD_CMS_PAGE = 'LOAD_CMS_PAGE';
export const loadCmsPage = ({ path, shouldIgnoreUnhandledError }) => ({
  type: LOAD_CMS_PAGE,
  path,
  shouldIgnoreUnhandledError,
});

export function* trackUserData(client, msaId, msaAlias, user) {
  try {
    const cartAbandoner = memberStatus('UNSUBSCRIBED', deepExtract('membership.status', user));
    if (cartAbandoner) {
      yield call(segment.identify, user.id, {
        msaId,
        msaName: msaAlias,
      });
    }
    yield call(trackGoogleTagManagerEvent, {
      msaId,
      msaName: msaAlias,
      userId: cartAbandoner && user.id,
    });
  } catch (error) {
    /* noop */
  }
}

export function* doRedirectUser(status) {
  if (status === 'active' || status === 'unpaid') {
    if (__CLIENT__ && !__TEST__) {
      window.location.href = '/search';
      return;
    }
    return yield put(updatePath('/search'));
  }

  if (status === 'unsubscribed') {
    return yield put(updatePath('/signup/continue'));
  }

  if (status === 'cancelled') {
    return yield put(updatePath('/rejoin'));
  }
}

export function* doInitializeMsa(client, path) {
  const msa = (yield call(getMsaFromPath, client, path)) || (yield call(geodetectMsa, client)) || {};

  if (msa.package_purchase) {
    yield put(setMsaId(msa.id));
  }

  return msa;
}

export function* doLoadCmsPage(client, { path, shouldIgnoreUnhandledError }) {
  try {
    const msa = yield call(doInitializeMsa, client, path);
    const msaId = msa.id;
    const msaAlias = msa.alias;

    const cmsData = yield call(fetchEntityIfNeeded, cms, [
      client,
      path,
      {
        // CMS should be able to handle non-purchasable msa-aliases
        // This is a temporary client-side fix for cms service bug: Ticket: https://classpass.atlassian.net/browse/CRO-454
        ...(msaAlias && msa.package_purchase && { msa_alias: msaAlias }),
      },
    ]);

    if (__CLIENT__ && cmsData.metadata.cpOptimize) {
      try {
        yield call(writeSessionStorage, 'cpOptimize', cmsData.metadata.cpOptimize);
      } catch (err) {
        /* noop */
      }
    }

    const profile = (yield select(isLoggedIn)) ? yield call(fetchEntityIfNeeded, userProfile, [client]) : null;

    if (profile) {
      const status = deepExtract('membership.status', profile);
      const audience = cmsData.metadata.audience || [];
      const shouldSeePage = audience.indexOf(status) !== -1;

      if (!shouldSeePage) {
        yield call(doRedirectUser, status);
        return;
      }
    }

    yield spawn(trackUserData, client, msaId, msaAlias, profile);
    yield call(doLoadCmsModules, client, cmsData);
    yield call(scrollToTop);
    return cmsData;
  } catch (error) {
    if (error && error.httpStatus === 404) {
      const [, redirectTo] =
        [['/try/', '/try'], ['/rejoin/', '/rejoin']].find(([pathPrefix]) => path.indexOf(pathPrefix) === 0) || [];

      if (redirectTo) {
        return yield put(
          replacePath({
            pathname: redirectTo,
            state: { status: 301 },
          })
        );
      }
    }
    if (!shouldIgnoreUnhandledError) {
      throw error;
    }
  }
}

export function* watchCmsPageSagas(client) {
  yield takeEvery(LOAD_CMS_PAGE, doLoadCmsPage, client);
}
